/**
 * The town's painted textures, drawn on 2D canvases (lifted from the shared
 * prototype engine's `engine/src/art.js`: `texture`, `reliefTexture`,
 * `contactTexture`, `pennantTexture`, `foliageTexture`, `facadeAtlas`,
 * `glowTexture`, `signTexture`, `botanicalTexture`, `clockTexture`; and
 * lantern-water's `window-art.js`: `windowAtlas`, `lanternPaneTexture`). Each
 * draws from its own seeded generator, so a texture never depends on what was
 * built before it. Not lifted: `characterTexture` (characters are sprites from
 * the sprites repo, or the renderer's proxies).
 *
 * No text is painted (ADR 0005: text is DOM, and every text is a string key):
 * the signboards keep their frame and ground without lettering, the botanical
 * plate and the ledger keep their drawings with ruled lines where the
 * prototype wrote words, and the clock has hour ticks instead of numerals.
 * The procedural arithmetic stays as it was lifted (the renderer's rule).
 */
import * as THREE from 'three';
import { seededRandom as rng } from '../grid.ts';

type Ctx2d = CanvasRenderingContext2D;

const canvas = (w: number, h: number = w): readonly [HTMLCanvasElement, Ctx2d] => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (g === null) throw new Error('no 2D canvas context for the town textures');
  return [c, g];
};

const tex = (c: HTMLCanvasElement): THREE.CanvasTexture => {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestMipmapLinearFilter;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  return t;
};

export type SurfaceKind = 'stone' | 'cobble' | 'roof' | 'slate' | 'wood' | 'plaster' | 'rug' | 'grass';
export const surfaceKinds: readonly SurfaceKind[] = ['stone', 'cobble', 'roof', 'slate', 'wood', 'plaster', 'rug', 'grass'];

/** A 128² pixel-art surface (the procedural fallback of each town texture, and the only one of the others). */
export const texture = (kind: SurfaceKind): THREE.CanvasTexture => {
  const [c, g] = canvas(128);
  const r = rng(83 + kind.length);
  if (kind === 'stone' || kind === 'cobble') {
    g.fillStyle = kind === 'stone' ? '#595951' : '#74776a';
    g.fillRect(0, 0, 128, 128);
    const rowHeight = 16;
    for (let y = 0; y < 128; y += rowHeight) {
      let x = -32 + ((y / rowHeight) % 2) * 13;
      while (x < 128) {
        const width = kind === 'stone' ? 25 + Math.floor(r() * 13) : 12 + Math.floor(r() * 14);
        const sx = x + 1;
        const sy = y + 1 + r() * 2;
        const w = width - 2;
        const h = rowHeight - 3;
        const chip = 2 + r() * 2;
        const light = kind === 'stone' ? 39 + r() * 15 : 42 + r() * 17;
        const hue = kind === 'stone' ? ([32, 215, 265][Math.floor(r() * 3)] ?? 32) : 40 + r() * 21;
        g.fillStyle = `hsl(${hue} ${kind === 'stone' ? 5 : 12}% ${light}%)`;
        g.beginPath();
        g.moveTo(sx + chip, sy);
        g.lineTo(sx + w - 2, sy + 1);
        g.lineTo(sx + w, sy + 3);
        g.lineTo(sx + w - 1, sy + h - 2);
        g.lineTo(sx + w - 4, sy + h);
        g.lineTo(sx + 1, sy + h - 1);
        g.lineTo(sx, sy + 3);
        g.closePath();
        g.fill();
        g.strokeStyle = `hsla(38,9%,${light + 24}%,.45)`;
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(sx + 2, sy + h - 3);
        g.lineTo(sx + 1, sy + 3);
        g.lineTo(sx + chip, sy + 1);
        g.lineTo(sx + w - 3, sy + 1);
        g.stroke();
        g.fillStyle = '#29383144';
        g.fillRect(sx + 3, sy + h - 1, w - 6, 1);
        for (let j = 0; j < 20; j += 1) {
          g.fillStyle = j % 3 ? '#e0d4b71a' : '#263c381f';
          g.fillRect(sx + 2 + r() * (w - 4), sy + 2 + r() * (h - 4), 1 + r() * 3, 1 + r() * 2);
        }
        if (r() < 0.12) {
          g.fillStyle = '#65754d66';
          g.fillRect(sx + 2, sy + h - 1, 2 + r() * 6, 2);
        }
        x += width;
      }
    }
  } else if (kind === 'roof' || kind === 'slate') {
    g.fillStyle = kind === 'slate' ? '#303d40' : '#503c31';
    g.fillRect(0, 0, 128, 128);
    const colors = kind === 'slate' ? ['#536c6e', '#6b7b78', '#728782', '#526564', '#85958a'] : ['#965d45', '#a86d4b', '#ae7255', '#94644d', '#b9825a'];
    for (let y = -12; y < 128; y += 12) {
      for (let x = -16; x < 128; x += 16) {
        const xx = x + ((Math.abs(y) / 12) % 2) * 8;
        g.fillStyle = colors[Math.floor(r() * colors.length)] ?? '#000000';
        g.fillRect(xx + 1, y, 14, 11);
        g.fillRect(xx + 3, y + 11, 10, 1);
        g.fillStyle = kind === 'slate' ? '#b5cab54a' : '#efc08e55';
        g.fillRect(xx + 2, y + 1, 2, 9);
        g.fillRect(xx + 3, y + 1, 9, 1);
        g.fillStyle = '#242b2a35';
        g.fillRect(xx + 12, y + 2, 2, 9);
        g.fillRect(xx + 3, y + 10, 9, 1);
        for (let i = 0; i < 14; i += 1) {
          g.fillStyle = i % 3 ? '#f7d9ab15' : '#403c3329';
          g.fillRect(xx + 3 + r() * 9, y + 2 + r() * 7, 1 + r() * 2, 1);
        }
        if (r() < 0.13) {
          g.fillStyle = '#7c865952';
          g.fillRect(xx + 3, y + 8, 4 + r() * 7, 2);
        }
      }
    }
  } else if (kind === 'wood') {
    g.fillStyle = '#604a36';
    g.fillRect(0, 0, 128, 128);
    for (let x = 0; x < 128; x += 16) {
      g.fillStyle = ['#69513b', '#7d6042', '#8a6b47', '#705239'][Math.floor(r() * 4)] ?? '#69513b';
      g.fillRect(x + 1, 0, 14, 128);
      g.fillStyle = '#b08a5140';
      g.fillRect(x + 2, 0, 1, 128);
      for (let i = 0; i < 30; i += 1) {
        g.fillStyle = r() < 0.5 ? '#312b252a' : '#d3a56724';
        g.fillRect(x + 2 + r() * 11, r() * 128, 1, 4 + r() * 25);
      }
    }
  } else if (kind === 'plaster') {
    g.fillStyle = '#c9c2b2';
    g.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 2000; i += 1) {
      g.fillStyle = ['#e6d7b426', '#87755b20', '#bc9b7033'][i % 3] ?? '#e6d7b426';
      g.fillRect(r() * 128, r() * 128, 1 + r() * 5, 1 + r() * 4);
    }
    for (let i = 0; i < 10; i += 1) {
      const x = r() * 128;
      const y = 114 + r() * 14;
      g.fillStyle = '#746f523d';
      g.fillRect(x, y, 8 + r() * 15, 14);
    }
    for (let i = 0; i < 9; i += 1) {
      const x = r() * 128;
      const y = r() * 128;
      g.strokeStyle = '#75685435';
      g.lineWidth = 0.7;
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(x + 2, y + 7);
      g.lineTo(x - 1, y + 10);
      g.lineTo(x + 1, y + 13);
      g.stroke();
    }
  } else if (kind === 'rug') {
    g.fillStyle = '#743b39';
    g.fillRect(0, 0, 128, 128);
    for (let i = 5; i < 20; i += 5) {
      g.strokeStyle = i % 2 ? '#b89463' : '#344e52';
      g.lineWidth = 3;
      g.strokeRect(i, i, 128 - 2 * i, 128 - 2 * i);
    }
    for (let x = 28; x < 110; x += 24) {
      for (let y = 28; y < 110; y += 24) {
        g.save();
        g.translate(x, y);
        g.rotate(Math.PI / 4);
        g.fillStyle = '#caab75';
        g.fillRect(-5, -5, 10, 10);
        g.fillStyle = '#355a5c';
        g.fillRect(-2, -2, 4, 4);
        g.restore();
      }
    }
  } else {
    g.fillStyle = '#687457';
    g.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 20; i += 1) {
      const x = r() * 128;
      const y = r() * 128;
      const radius = 9 + r() * 15;
      g.fillStyle = i % 2 ? '#53674f24' : '#8a8c6024';
      for (const dx of [-128, 0, 128]) {
        for (const dy of [-128, 0, 128]) {
          g.beginPath();
          g.ellipse(x + dx, y + dy, radius, radius * 0.6, 0, 0, Math.PI * 2);
          g.fill();
        }
      }
    }
    for (let i = 0; i < 1400; i += 1) {
      g.fillStyle = ['#75806066', '#53654f55', '#85876555', '#63725455'][i % 4] ?? '#75806066';
      g.fillRect(r() * 128, r() * 128, 1 + r() * 2, 1 + r());
    }
  }
  for (let i = 0; i < 2400; i += 1) {
    g.fillStyle = r() < 0.5 ? '#ffffff08' : '#0000000c';
    g.fillRect(r() * 128, r() * 128, 1, 1);
  }
  return tex(c);
};

/** Paints `source`'s luminance into `target`'s canvas (a relief map): at build and again when artwork replaces the source image. */
export const paintRelief = (target: THREE.CanvasTexture, source: TexImageSource & { readonly width: number; readonly height: number }): void => {
  const c = target.image as HTMLCanvasElement;
  c.width = source.width;
  c.height = source.height;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (g === null) return;
  g.drawImage(source as CanvasImageSource, 0, 0);
  const im = g.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < im.data.length; i += 4) {
    const v = 0.2126 * (im.data[i] ?? 0) + 0.7152 * (im.data[i + 1] ?? 0) + 0.0722 * (im.data[i + 2] ?? 0);
    im.data[i] = v;
    im.data[i + 1] = v;
    im.data[i + 2] = v;
  }
  g.putImageData(im, 0, 0);
  target.needsUpdate = true;
};

/** A grey relief (bump) map from a texture's luminance. */
export const reliefTexture = (source: THREE.CanvasTexture): THREE.CanvasTexture => {
  const [c] = canvas(1);
  const t = tex(c);
  t.colorSpace = THREE.NoColorSpace;
  paintRelief(t, source.image as HTMLCanvasElement);
  return t;
};

/** The soft contact shadow under boxes standing on the ground. */
export const contactTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(128);
  const data = g.createImageData(128, 128);
  for (let y = 0; y < 128; y += 1) {
    for (let x = 0; x < 128; x += 1) {
      const d = Math.pow(Math.pow(Math.abs((x - 64) / 64), 4) + Math.pow(Math.abs((y - 64) / 64), 4), 0.25);
      const a = Math.max(0, 1 - d);
      const i = (y * 128 + x) * 4;
      data.data[i] = 12;
      data.data[i + 1] = 23;
      data.data[i + 2] = 21;
      data.data[i + 3] = Math.min(255, a * a * 760);
    }
  }
  g.putImageData(data, 0, 0);
  const t = tex(c);
  t.magFilter = THREE.LinearFilter;
  return t;
};

export const pennantTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(64, 96);
  const r = rng(9251);
  g.fillStyle = '#dfd9cb';
  g.fillRect(0, 0, 64, 96);
  for (let y = 0; y < 96; y += 2) {
    g.fillStyle = y % 4 ? '#6f695c10' : '#ffffff13';
    g.fillRect(0, y, 64, 1);
  }
  for (let i = 0; i < 700; i += 1) {
    g.fillStyle = i % 3 ? '#ffffff16' : '#655f5115';
    g.fillRect(Math.floor(r() * 64), Math.floor(r() * 96), 1, 2);
  }
  g.fillStyle = '#eee7d2';
  g.fillRect(2, 0, 2, 96);
  g.fillRect(60, 0, 2, 96);
  g.fillRect(0, 5, 64, 2);
  g.beginPath();
  g.moveTo(32, 23);
  g.lineTo(46, 44);
  g.lineTo(32, 66);
  g.lineTo(18, 44);
  g.closePath();
  g.fill();
  g.fillStyle = '#858174';
  g.fillRect(30, 35, 4, 18);
  g.fillRect(25, 42, 14, 4);
  const t = tex(c);
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
};

export const foliageTexture = (autumn = false): THREE.CanvasTexture => {
  const [c, g] = canvas(128);
  const r = rng(autumn ? 988 : 771);
  const palette = autumn ? ['#4e4931', '#74643a', '#978042', '#b49a50', '#c6ae66', '#e0c784'] : ['#273c30', '#3d5437', '#506c43', '#738653', '#91a467', '#afbb83'];
  const clusters = Array.from({ length: 14 }, () => ({ x: 22 + r() * 84, y: 18 + r() * 79, rx: 13 + r() * 13, ry: 11 + r() * 17 })).sort((a, b) => a.y - b.y);
  g.strokeStyle = autumn ? '#574a32' : '#384432';
  g.lineWidth = 2;
  for (const k of clusters) {
    g.beginPath();
    g.moveTo(64, 124);
    g.quadraticCurveTo(62, 80, k.x, k.y);
    g.stroke();
  }
  for (const k of clusters) {
    for (let i = 0; i < 250; i += 1) {
      const x = k.x + (r() - 0.5) * k.rx * 2;
      const y = k.y + (r() - 0.5) * k.ry * 2;
      const nx = (x - k.x) / k.rx;
      const ny = (y - k.y) / k.ry;
      if (nx * nx + ny * ny > 1 - r() * 0.15 || x < 3 || x > 125 || y < 3 || y > 121) continue;
      const shade = Math.sqrt(1 - nx * nx - ny * ny) * 0.4 + (-nx * 0.5 - ny * 0.8) * 0.18 + (1 - y / 128) * 0.2 + r() * 0.25;
      g.fillStyle = palette[Math.max(0, Math.min(5, Math.floor(shade * 6)))] ?? '#000000';
      g.fillRect(Math.floor(x), Math.floor(y), 2 + Math.floor(r() * 4), 1 + Math.floor(r() * 3));
      if (r() < 0.13) {
        g.fillStyle = palette[Math.min(5, Math.floor(shade * 6) + 1)] ?? '#000000';
        g.fillRect(Math.floor(x), Math.floor(y), 2, 1);
      }
    }
  }
  return tex(c);
};

export type FacadeTile = { readonly u: number; readonly v: number; readonly du: number; readonly dv: number };
export type FacadeAtlas = { readonly map: THREE.CanvasTexture; readonly heightMap: THREE.CanvasTexture; paint(width: number, height: number, type: number): FacadeTile };

/** One atlas of up to eight weathered house walls, so they share one material (allocated per view). */
export const facadeAtlas = (): FacadeAtlas => {
  const [c, g] = canvas(1024, 512);
  const [hc, hg] = canvas(1024, 512);
  const map = tex(c);
  const heightMap = tex(hc);
  heightMap.colorSpace = THREE.NoColorSpace;
  heightMap.wrapS = THREE.ClampToEdgeWrapping;
  heightMap.wrapT = THREE.ClampToEdgeWrapping;
  map.wrapS = THREE.ClampToEdgeWrapping;
  map.wrapT = THREE.ClampToEdgeWrapping;
  let next = 0;
  return {
    map,
    heightMap,
    paint(width, height, type) {
      const index = next;
      next += 1;
      if (index >= 8) throw new Error('Facade atlas capacity exceeded');
      const px = (index % 4) * 256;
      const py = Math.floor(index / 4) * 256;
      const r = rng(5107 + index * 733);
      const hr = rng(9127 + index * 173);
      g.save();
      hg.save();
      g.translate(px, py);
      hg.translate(px, py);
      hg.beginPath();
      hg.rect(0, 0, 256, 256);
      hg.clip();
      hg.fillStyle = '#808080';
      hg.fillRect(0, 0, 256, 256);
      for (let i = 0; i < 3300; i += 1) {
        hg.fillStyle = i % 3 ? '#ffffff0a' : '#00000012';
        hg.fillRect(Math.floor(hr() * 256), Math.floor(hr() * 256), 1 + Math.floor(hr() * 3), 1 + Math.floor(hr() * 2));
      }
      g.beginPath();
      g.rect(0, 0, 256, 256);
      g.clip();
      g.fillStyle = ['#c4c7bb', '#d7cabc', '#b9c4c9', '#c5cbd0', '#c6b0a7'][type] ?? '#c4c7bb';
      g.fillRect(0, 0, 256, 256);
      for (let i = 0; i < 80; i += 1) {
        const x = r() * 256;
        const y = r() * 256;
        const rad = 8 + r() * 29;
        const wash = g.createRadialGradient(x, y, 0, x, y, rad);
        wash.addColorStop(0, i % 3 ? '#eee5cc13' : '#58665d12');
        wash.addColorStop(1, '#83918100');
        g.fillStyle = wash;
        g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
      }
      for (let i = 0; i < 5100; i += 1) {
        g.fillStyle = ['#f6eddd21', '#7c827420', '#a5a18b20', '#5967560e'][i % 4] ?? '#f6eddd21';
        g.fillRect(Math.floor(r() * 256), Math.floor(r() * 256), 1 + Math.floor(r() * 3), 1 + Math.floor(r() * 2));
      }
      for (const y of [0, 256 * (1 - 0.54)]) {
        const grime = g.createLinearGradient(0, y, 0, y + 12);
        grime.addColorStop(0, '#3d484b38');
        grime.addColorStop(1, '#535e5300');
        g.fillStyle = grime;
        g.fillRect(0, y, 256, 12);
      }
      for (const cx of [256 * 0.21, 256 * 0.79]) {
        for (const center of [height * 0.73, 1.42]) {
          const start = 256 * (1 - (center - 0.62) / height);
          for (let i = 0; i < 15; i += 1) {
            const x = cx + (((r() - 0.5) * 0.98) / width) * 256;
            const length = 5 + r() * 30;
            const drip = g.createLinearGradient(0, start, 0, start + length);
            drip.addColorStop(0, '#5a68573a');
            drip.addColorStop(1, '#63775e00');
            g.fillStyle = drip;
            g.fillRect(Math.floor(x), Math.floor(start), 1 + Math.floor(r() * 3), length);
          }
        }
      }
      for (let k = 0; k < 3; k += 1) {
        const x = 12 + r() * 218;
        const y = 193 + r() * 37;
        const ww = 13 + r() * 20;
        const hh = 9 + r() * 15;
        g.save();
        hg.save();
        const patch = new Path2D();
        for (let i = 0; i < 12; i += 1) {
          const a = (i * Math.PI) / 6;
          const rad = 0.67 + r() * 0.33;
          const xx = x + Math.cos(a) * ww * rad;
          const yy = y + Math.sin(a) * hh * rad;
          if (i === 0) patch.moveTo(xx, yy);
          else patch.lineTo(xx, yy);
        }
        patch.closePath();
        g.clip(patch);
        hg.clip(patch);
        g.fillStyle = '#727367';
        g.fillRect(x - ww, y - hh, ww * 2, hh * 2);
        hg.fillStyle = '#373737';
        hg.fillRect(x - ww, y - hh, ww * 2, hh * 2);
        for (let yy = Math.floor((y - hh) / 6) * 6; yy < y + hh; yy += 6) {
          for (let xx = Math.floor((x - ww) / 15) * 15 - 15; xx < x + ww; xx += 15) {
            const shift = (Math.floor(yy / 6) % 2) * 7;
            g.fillStyle = ['#a99c83', '#b0a48a', '#8f947e', '#b1aa95'][Math.floor(r() * 4)] ?? '#a99c83';
            g.fillRect(xx + shift + 1, yy + 1, 13, 4);
            hg.fillStyle = '#686868';
            hg.fillRect(xx + shift + 1, yy + 1, 13, 4);
            g.fillStyle = '#dbceab50';
            g.fillRect(xx + shift + 1, yy + 1, 11, 1);
          }
        }
        g.restore();
        hg.restore();
      }
      const damp = g.createLinearGradient(0, 215, 0, 256);
      damp.addColorStop(0, '#43584500');
      damp.addColorStop(1, '#43584548');
      g.fillStyle = damp;
      g.fillRect(0, 215, 256, 41);
      g.strokeStyle = '#56635c50';
      g.lineWidth = 1;
      hg.strokeStyle = '#484848';
      hg.lineWidth = 1;
      for (let i = 0; i < 4; i += 1) {
        const x = 8 + r() * 240;
        const y = 25 + r() * 180;
        const crack = new Path2D();
        crack.moveTo(x, y);
        crack.lineTo(x - 2, y + 5);
        crack.lineTo(x + 1, y + 9);
        crack.lineTo(x, y + 15);
        crack.moveTo(x + 1, y + 9);
        crack.lineTo(x + 5, y + 11);
        g.stroke(crack);
        hg.stroke(crack);
      }
      g.restore();
      hg.restore();
      map.needsUpdate = true;
      heightMap.needsUpdate = true;
      return { u: (px + 2) / 1024, v: 1 - (py + 254) / 512, du: 252 / 1024, dv: 252 / 512 };
    },
  };
};

export const glowTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(64);
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, '#fff7dfff');
  gr.addColorStop(0.15, '#ffdc9bbb');
  gr.addColorStop(0.4, '#ffb35444');
  gr.addColorStop(1, '#ffb35400');
  g.fillStyle = gr;
  g.fillRect(0, 0, 64, 64);
  return tex(c);
};

/** A signboard's painted ground and double frame (`signTexture` without its lettering: text is DOM, ADR 0005). */
export const signTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(256, 128);
  g.fillStyle = '#203c3c';
  g.fillRect(0, 0, 256, 128);
  g.strokeStyle = '#bca273';
  g.lineWidth = 3;
  g.strokeRect(7, 7, 242, 114);
  g.strokeRect(12, 12, 232, 104);
  // A painted rule and two diamonds where the name was lettered.
  g.fillStyle = '#eed6a4';
  g.fillRect(64, 62, 128, 3);
  for (const x of [52, 204]) {
    g.beginPath();
    g.moveTo(x, 57);
    g.lineTo(x + 6, 63);
    g.lineTo(x, 69);
    g.lineTo(x - 6, 63);
    g.closePath();
    g.fill();
  }
  return tex(c);
};

/** A botanical plate (or, `ledger`, the open recipe book), drawn without its captions: ruled ink lines stand for the writing. */
export const botanicalTexture = (ledger = false): THREE.CanvasTexture => {
  const [c, g] = canvas(ledger ? 256 : 128, 192);
  const random = rng(ledger ? 621 : 482);
  g.fillStyle = '#d3c29b';
  g.fillRect(0, 0, c.width, c.height);
  for (let i = 0; i < 1600; i += 1) {
    g.fillStyle = i % 3 ? '#90745312' : '#fff0ca19';
    g.fillRect(Math.floor(random() * c.width), Math.floor(random() * 192), 1 + random() * 4, 1);
  }
  g.strokeStyle = '#8d805e';
  g.lineWidth = 1;
  g.strokeRect(6, 6, c.width - 12, 180);
  const rule = (x: number, y: number, w: number, color: string): void => {
    g.fillStyle = color;
    g.fillRect(Math.round(x - w / 2), y, Math.round(w), 1);
  };
  rule(c.width / 2, 17, 44, '#4e5848');
  const plant = (cx: number, base: number, scale: number): void => {
    g.strokeStyle = '#546b49';
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(cx, base);
    g.bezierCurveTo(cx - 9 * scale, base - 40 * scale, cx + 12 * scale, base - 75 * scale, cx, base - 111 * scale);
    g.stroke();
    for (let i = 0; i < 8; i += 1) {
      const yy = base - 12 * scale - i * 11 * scale;
      const side = i % 2 ? 1 : -1;
      const xx = cx + Math.sin(i * 0.8) * 5 * scale;
      g.fillStyle = i % 3 ? '#637953' : '#86905c';
      g.beginPath();
      g.moveTo(xx, yy);
      g.bezierCurveTo(xx + side * 22 * scale, yy + 3 * scale, xx + side * 27 * scale, yy - 12 * scale, xx + side * 22 * scale, yy - 17 * scale);
      g.bezierCurveTo(xx + side * 6 * scale, yy - 18 * scale, xx + side * 4 * scale, yy - 5 * scale, xx, yy);
      g.fill();
      g.strokeStyle = '#b4ba7d';
      g.lineWidth = 0.7;
      g.beginPath();
      g.moveTo(xx, yy);
      g.lineTo(xx + side * 21 * scale, yy - 15 * scale);
      g.stroke();
    }
    for (let i = 0; i < 5; i += 1) {
      g.fillStyle = i % 2 ? '#9b6f76' : '#b18a93';
      g.beginPath();
      g.ellipse(cx + (random() - 0.5) * 9 * scale, base - 108 * scale - i * 3 * scale, 3 * scale, 5 * scale, -0.3, 0, Math.PI * 2);
      g.fill();
    }
  };
  plant(ledger ? 65 : 64, 150, ledger ? 0.83 : 1);
  rule(ledger ? 65 : 64, 167, 50, '#5f614d');
  if (ledger) {
    g.strokeStyle = '#7b674e';
    g.beginPath();
    g.moveTo(128, 8);
    g.lineTo(126, 184);
    g.stroke();
    // The recipe's seven lines as ink rules of their lengths.
    const lengths = [72, 74, 52, 70, 64, 0, 82];
    lengths.forEach((w, i) => {
      if (w > 0) rule(143 + w / 2, 46 + i * 15, w, '#70624c');
    });
    g.strokeStyle = '#79634d66';
    g.beginPath();
    g.ellipse(223, 155, 13, 10, 0.3, 0, 6.28);
    g.stroke();
  } else {
    rule(64, 177, 76, '#857657');
  }
  return tex(c);
};

/** The clock face, with hour ticks where the prototype painted numerals. */
export const clockTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(128);
  g.fillStyle = '#d7c89e';
  g.beginPath();
  g.arc(64, 64, 61, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = '#6e5b3e';
  g.lineWidth = 2;
  g.beginPath();
  g.arc(64, 64, 55, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = '#49493a';
  for (let i = 0; i < 12; i += 1) {
    const a = (i * Math.PI) / 6 - Math.PI / 2;
    g.lineWidth = i % 3 === 0 ? 4 : 2;
    g.beginPath();
    g.moveTo(64 + Math.cos(a) * 38, 64 + Math.sin(a) * 38);
    g.lineTo(64 + Math.cos(a) * 48, 64 + Math.sin(a) * 48);
    g.stroke();
  }
  g.strokeStyle = '#394638';
  g.lineWidth = 3;
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(64, 64);
  g.lineTo(87, 77);
  g.moveTo(64, 64);
  g.lineTo(29, 64);
  g.stroke();
  g.fillStyle = '#a48046';
  g.fillRect(61, 61, 6, 6);
  return tex(c);
};

/** A small linear congruential generator (`window-art.js`'s own). */
const seeded = (start: number): (() => number) => {
  let seed = start;
  return () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
};

const clampedTex = (c: HTMLCanvasElement): THREE.CanvasTexture => {
  const t = tex(c);
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
};

export type WindowAtlas = { readonly map: THREE.CanvasTexture; readonly emission: THREE.CanvasTexture; readonly columns: number; readonly rows: number };

/** Four leaded pane drawings and their separate interior-glow masks (`window-art.js`). */
export const windowAtlas = (cool = false): WindowAtlas => {
  const [color, c] = canvas(256, 384);
  const [light, l] = canvas(256, 384);
  for (let tile = 0; tile < 4; tile += 1) {
    const x = (tile % 2) * 128;
    const y = Math.floor(tile / 2) * 192;
    const r = seeded(8307 + tile * 73 + (cool ? 193 : 0));
    for (const g of [c, l]) {
      g.save();
      g.translate(x, y);
      g.beginPath();
      g.rect(0, 0, 128, 192);
      g.clip();
    }
    c.fillStyle = cool ? '#30454b' : '#514d40';
    c.fillRect(0, 0, 128, 192);
    l.fillStyle = '#070706';
    l.fillRect(0, 0, 128, 192);
    const reflection = c.createLinearGradient(0, 0, 96, 192);
    reflection.addColorStop(0, cool ? '#597b89' : '#998e6d');
    reflection.addColorStop(0.38, cool ? '#8caaaa' : '#c1af83');
    reflection.addColorStop(0.7, cool ? '#4c686c' : '#8f805e');
    reflection.addColorStop(1, cool ? '#314a50' : '#67634f');
    c.fillStyle = reflection;
    c.fillRect(5, 5, 118, 182);
    const lit = l.createRadialGradient(69, 132, 8, 63, 105, 130);
    lit.addColorStop(0, '#eee0bd');
    lit.addColorStop(0.57, '#aa9c78');
    lit.addColorStop(1, '#61573f');
    l.fillStyle = lit;
    l.fillRect(5, 5, 118, 182);
    for (let i = 0; i < 1100; i += 1) {
      c.fillStyle = i % 2 ? '#e7efda10' : '#25373410';
      c.fillRect(5 + Math.floor(r() * 116), 5 + Math.floor(r() * 180), 1 + Math.floor(r() * 3), 1 + Math.floor(r() * 4));
    }
    if (tile === 2 || tile === 3) {
      for (const side of [-1, 1]) {
        const path = (g: Ctx2d): void => {
          g.beginPath();
          g.moveTo(side < 0 ? 5 : 123, 6);
          g.lineTo(side < 0 ? 38 : 91, 6);
          g.bezierCurveTo(side < 0 ? 34 : 94, 65, side < 0 ? 23 : 106, 95, side < 0 ? 15 : 114, 185);
          g.lineTo(side < 0 ? 5 : 123, 185);
          g.closePath();
        };
        c.fillStyle = cool ? '#65776c' : '#b8a17c';
        path(c);
        c.fill();
        l.fillStyle = '#534934';
        path(l);
        l.fill();
        for (let i = 0; i < 4; i += 1) {
          c.strokeStyle = i % 2 ? '#d5c49e80' : '#554e3960';
          c.lineWidth = 2;
          c.beginPath();
          c.moveTo(side < 0 ? 10 + i * 6 : 118 - i * 6, 8);
          c.quadraticCurveTo(side < 0 ? 12 + i * 3 : 116 - i * 3, 90, side < 0 ? 9 + i * 2 : 119 - i * 2, 182);
          c.stroke();
        }
      }
    }
    c.fillStyle = cool ? '#d4e3d835' : '#ece8cf2a';
    c.beginPath();
    c.moveTo(31, 5);
    c.lineTo(46, 5);
    c.lineTo(6, 107);
    c.lineTo(6, 79);
    c.closePath();
    c.fill();
    const lead = (ax: number, ay: number, bx: number, by: number, width = 2): void => {
      c.strokeStyle = '#334345';
      c.lineWidth = width;
      c.beginPath();
      c.moveTo(ax, ay);
      c.lineTo(bx, by);
      c.stroke();
      l.strokeStyle = '#030403';
      l.lineWidth = width + 1;
      l.beginPath();
      l.moveTo(ax, ay);
      l.lineTo(bx, by);
      l.stroke();
      c.strokeStyle = '#b4b09788';
      c.lineWidth = 0.6;
      c.beginPath();
      c.moveTo(ax + 0.8, ay);
      c.lineTo(bx + 0.8, by);
      c.stroke();
    };
    if (tile === 0 || tile === 3) {
      for (let a = -190; a < 150; a += 32) {
        lead(a, 5, a + 118, 187, 1.7);
        lead(a + 118, 5, a, 187, 1.7);
      }
    } else {
      for (const xx of [43, 85]) lead(xx, 5, xx, 187);
      for (const yy of [42, 84, 126, 168]) lead(5, yy, 123, yy);
    }
    if (tile === 1 || tile === 3) {
      const px = tile === 1 ? 25 : 96;
      for (const g of [c, l]) {
        g.fillStyle = g === c ? '#4c594b' : '#070906';
        g.fillRect(px - 8, 168, 16, 16);
        g.fillStyle = g === c ? '#546b50' : '#10150b';
        g.fillRect(px - 1, 131, 2, 39);
        for (let i = 0; i < 6; i += 1) {
          const side = i % 2 ? 1 : -1;
          g.beginPath();
          g.ellipse(px + side * 6, 140 + i * 4, 8, 3, side * 0.45, 0, Math.PI * 2);
          g.fill();
        }
      }
    }
    for (const g of [c, l]) {
      g.strokeStyle = g === c ? '#394342' : '#040403';
      g.lineWidth = 4;
      g.strokeRect(4, 4, 120, 184);
      g.restore();
    }
  }
  return { map: clampedTex(color), emission: clampedTex(light), columns: 2, rows: 2 };
};

/** The lantern's glass panes. */
export const lanternPaneTexture = (): THREE.CanvasTexture => {
  const [c, g] = canvas(64, 96);
  const gradient = g.createRadialGradient(32, 60, 4, 32, 49, 53);
  gradient.addColorStop(0, '#fff0b5');
  gradient.addColorStop(0.21, '#ecd084');
  gradient.addColorStop(0.6, '#b58a43');
  gradient.addColorStop(1, '#503e27');
  g.fillStyle = gradient;
  g.fillRect(0, 0, 64, 96);
  g.fillStyle = '#fff5d064';
  g.fillRect(11, 16, 2, 54);
  g.fillStyle = '#5e4a3340';
  g.fillRect(4, 4, 3, 88);
  g.fillRect(57, 4, 3, 88);
  for (let i = 0; i < 200; i += 1) {
    g.fillStyle = i % 2 ? '#ffeedb10' : '#403b3310';
    g.fillRect((i * 37) % 64, (i * 61) % 96, 1, 2);
  }
  return clampedTex(c);
};
